import { useQueryClient } from '@tanstack/react-query';
import React, { useState, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { CartContext } from '../../contexts/CartContext';
import { Cookie } from '../../tscapi/utils/cookie';
import Input from '../common/input';
import { useAvailableCredits } from '../myaccount/hooks/useAccount';

export const AccountCredit = () => {
    const { cartData, applyAccountCredit, clearAccountCredit, setIsLoading } = useContext(CartContext);
    const [creditAmount, setCreditAmount] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    const cookie: Cookie = new Cookie();
    let isOrderModMode = false;
    if (cookie.ModifyOrderNumber && cookie.ModifyOrderNumber.length > 0) {
        isOrderModMode = true;
    }

    const [hasCredit, setHasCredit] = useState<boolean>(() => !!cookie.UserAvailableCredit || !!cookie.TSC_HasCredit);
    const { data: creditsData } = useAvailableCredits(cartData?.Buyer?.CustomerEDP ?? 0, hasCredit ?? false);
    const totalCreditAmount = creditsData?.reduce((sum, credit) => sum + credit.CreditAmount, 0) ?? 0;

    const queryClient = useQueryClient();
    const { t } = useTranslation();

    useEffect(() => {
        const handleCreditChange = () => {
            setHasCredit(!!cookie.UserAvailableCredit || !!cookie.TSC_HasCredit);
        };

        window.addEventListener('account:statusChanged', handleCreditChange);

        return () => {
            window.removeEventListener('account:statusChanged', handleCreditChange);
        };
    }, [cookie.TSC_HasCredit, cookie.UserAvailableCredit]);

    if (!cartData || !hasCredit) return null;
    if (!creditsData) {
        return <div className="promocode__container formWrap" />;
    }

    const onAmountChange = (e) => {
        let value = e.target.value;

        value = value.replace(/[^0-9.]/g, '');

        const parts = value.split('.');

        if (parts.length > 2) {
            value = parts[0] + '.' + parts.slice(1).join('');
        }

        if (parts[1]) {
            value = `${parts[0]}.${parts[1].slice(0, 2)}`;
        }

        setErrorMessage('');
        setCreditAmount(value);
    };

    const applyCartAccountCredit = async () => {
        const amount = parseFloat(creditAmount);

        if (!creditAmount || isNaN(amount) || amount <= 0) {
            setErrorMessage(t('Please enter a valid amount.'));
            return;
        }

        if (amount > totalCreditAmount) {
            setErrorMessage(t('Amount exceeds available account credit.'));
            return;
        }

        try {
            setIsLoading(true);
            await applyAccountCredit(amount);

            queryClient.invalidateQueries({
                queryKey: ['availableCredits', cartData?.Buyer?.CustomerEDP],
            });
        } catch (error) {
            setErrorMessage(String(error));
        } finally {
            setIsLoading(false);
        }
    };

    const clearCartAccountCredit = async () => {
        try {
            setIsLoading(true);
            await clearAccountCredit();
            queryClient.invalidateQueries({ queryKey: ['availableCredits'] });
            setCreditAmount('');
        } catch (error) {
            setErrorMessage(String(error));
            setIsLoading(false);
        }
    };

    const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
        if (event.key === 'Enter') {
            event.preventDefault();
            event.stopPropagation();
            applyCartAccountCredit();
        }
    };

    if (cartData && hasCredit) {
        const remainingCredit = totalCreditAmount - (cartData?.OrderSummary?.AppliedCreditAmount || 0);
        const formattedRemainingCredit = new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(
            remainingCredit,
        );

        return (
            <>
                {!isOrderModMode && (
                    <div className="promocode__container formWrap">
                        <h3 className="promocode__title">{t('Enter Account Credit')}</h3>
                        {cartData?.OrderSummary?.AppliedCreditAmount > 0 ? (
                            <>
                                <div className="promoremove__container promocode__applied">
                                    <div className="form__input promocode__code promocode__applied__code">
                                        <span className="promocode__applied__value">
                                            {cartData?.OrderSummary?.AppliedCredit}
                                        </span>
                                        <span className="promocode__applied__balance">
                                            {t('Remaining Credits')}:{' '}
                                            <span className="promocode__applied__value">
                                                {formattedRemainingCredit}
                                            </span>
                                        </span>
                                    </div>
                                    <button className="credit__button--remove" onClick={clearCartAccountCredit}>
                                        {t('Remove')}
                                    </button>
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="credit__balance">
                                    {t('Initial Credits')}:{' '}
                                    <span className="credit__balance__value">
                                        {new Intl.NumberFormat('en-CA', {
                                            style: 'currency',
                                            currency: 'CAD',
                                        }).format(totalCreditAmount)}
                                    </span>
                                </div>
                                <Input
                                    className="form__input promocode__input"
                                    id="accountCreditAmount"
                                    error={errorMessage}
                                    errorCSS={'form__error-svg form__error-svg--black'}
                                    name="accountCreditAmount"
                                    value={creditAmount}
                                    type="text"
                                    inputMode="decimal"
                                    onChange={onAmountChange}
                                    onKeyDown={onKeyDown}
                                />

                                <div className="promocode__spacer"></div>

                                <button
                                    className="promocode__button--white button"
                                    onClick={applyCartAccountCredit}
                                    data-link-type="link"
                                    data-link-title="account credit"
                                    data-component-type="express checkout"
                                >
                                    {t('Apply')}
                                </button>
                            </>
                        )}
                    </div>
                )}
            </>
        );
    }
};
